import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { t } from "@/lib/i18n";
import "@/lib/i18n_p1";
import { CalendarPlus, Trash2, CalendarClock, Receipt, Briefcase, Scale, FileText, Home, Wallet, CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { Calendar as ShadCalendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const CATEGORY_ICON = {
  nssf: Wallet, vat: Receipt, payroll_tax: Briefcase, corporate_tax: Scale,
  personal_income_tax: Receipt, property_tax: Home, commercial_register: FileText, custom: CalendarClock,
};

export default function Calendar() {
  const { lang } = useLang();
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ title: "", date: "", note: "" });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const dateLocale = lang === "ar" ? "ar" : lang === "fr" ? "fr" : "en";
  const formattedDate = form.date
    ? new Date(form.date + "T00:00:00").toLocaleDateString(dateLocale, { year: "numeric", month: "long", day: "numeric" })
    : "";
  const toIsoYmd = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const load = () => api.get("/calendar/events", { params: { year: 2026 } }).then(({ data }) => setEvents(data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date) return;
    setBusy(true);
    try {
      await api.post("/calendar/events", form);
      setForm({ title: "", date: "", note: "" });
      load();
      toast.success("Reminder added");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || e2.message);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/calendar/events/${id}`);
      load();
    } catch (e) { toast.error(t(lang,"calendar.custom_only")); }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const ev of events) {
      const m = ev.date.slice(0, 7); // YYYY-MM
      g[m] = g[m] || [];
      g[m].push(ev);
    }
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [events]);

  const monthLabel = (ym) => {
    const [y, m] = ym.split("-");
    const d = new Date(parseInt(y), parseInt(m) - 1, 1);
    return d.toLocaleString(lang === "ar" ? "ar" : lang === "fr" ? "fr" : "en", { month: "long", year: "numeric" });
  };

  return (
    <AppShell>
      <div className="editorial-rule pt-4">
        <h1 className="font-display text-4xl">{t(lang,"calendar.title")}</h1>
      </div>
      <p className="mt-3 text-[#495057] max-w-2xl">{t(lang,"calendar.sub")}</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {grouped.length === 0 && (
            <div className="bg-white border border-[#E9ECEF] p-8 text-[#868E96]" data-testid="calendar-empty">{t(lang,"calendar.no_events")}</div>
          )}
          {grouped.map(([month, items]) => (
            <div key={month} className="mb-8" data-testid={`cal-month-${month}`}>
              <h3 className="text-xs tracking-[0.24em] uppercase font-bold text-[#C65D47] mb-3">{monthLabel(month)}</h3>
              <ul className="bg-white border border-[#E9ECEF] divide-y divide-[#E9ECEF]">
                {items.map((ev) => {
                  const Icon = CATEGORY_ICON[ev.category] || CalendarClock;
                  return (
                    <li key={ev.event_id} className="p-4 flex items-start gap-4" data-testid={`cal-event-${ev.event_id}`}>
                      <div className="w-12 text-center shrink-0">
                        <div className="font-display text-2xl text-[#0A1128]">{ev.date.slice(8, 10)}</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#868E96]">{new Date(ev.date).toLocaleString(lang === "ar" ? "ar" : lang === "fr" ? "fr" : "en", { weekday: "short" })}</div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Icon className="w-3.5 h-3.5 text-[#C65D47]" />
                          <span className="text-[10px] uppercase tracking-[0.18em] text-[#868E96]">{t(lang,`calendar.categories.${ev.category}`) || ev.category}</span>
                        </div>
                        <div className="text-sm font-medium mt-1">{ev.title?.[lang] || ev.title?.en}</div>
                        {ev.law_reference && <div className="text-xs text-[#868E96] mt-0.5">{ev.law_reference}</div>}
                        {ev.note && <div className="text-xs text-[#495057] mt-1">{ev.note}</div>}
                      </div>
                      {ev.scope === "user" && (
                        <button onClick={() => remove(ev.event_id)} className="text-[#868E96] hover:text-[#C92A2A]" data-testid={`cal-delete-${ev.event_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white border border-[#E9ECEF] p-6 sticky top-24">
            <h3 className="font-display text-xl flex items-center gap-2"><CalendarPlus className="w-4 h-4 text-[#C65D47]" /> {t(lang,"calendar.add_event")}</h3>
            <form onSubmit={create} className="mt-4 space-y-3">
              <div>
                <label className="text-xs tracking-[0.18em] uppercase text-[#868E96]">{t(lang,"calendar.event_title")}</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="cal-form-title" className="mt-2 w-full border border-[#E9ECEF] bg-[#F8F9FA] px-4 py-3 focus:outline-none focus:border-[#0A1128]" />
              </div>
              <div>
                <label className="text-xs tracking-[0.18em] uppercase text-[#868E96]">{t(lang,"calendar.event_date")}</label>
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="cal-form-date"
                      className={`mt-2 w-full border border-[#E9ECEF] bg-[#F8F9FA] px-4 py-3 text-start text-sm flex items-center justify-between focus:outline-none focus:border-[#0A1128] ${form.date ? "text-[#0A1128]" : "text-[#868E96]"}`}
                    >
                      <span>{formattedDate || t(lang,"calendar.event_date")}</span>
                      <CalendarIcon className="w-4 h-4 text-[#868E96]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="p-0 bg-white border border-[#E9ECEF]" data-testid="cal-form-date-popover">
                    <ShadCalendar
                      mode="single"
                      selected={form.date ? new Date(form.date + "T00:00:00") : undefined}
                      onSelect={(d) => {
                        if (!d) return;
                        setForm((prev) => ({ ...prev, date: toIsoYmd(d) }));
                        setDatePickerOpen(false);
                      }}
                      defaultMonth={new Date(2026, 0, 1)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-xs tracking-[0.18em] uppercase text-[#868E96]">{t(lang,"calendar.event_note")}</label>
                <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} data-testid="cal-form-note" className="mt-2 w-full border border-[#E9ECEF] bg-[#F8F9FA] px-4 py-3 focus:outline-none focus:border-[#0A1128]" />
              </div>
              <button type="submit" disabled={busy} className="w-full bg-[#0A1128] text-white px-4 py-3 hover:bg-[#1C2538] disabled:opacity-60" data-testid="cal-save-btn">
                {busy ? t(lang,"common.loading") : t(lang,"calendar.save")}
              </button>
            </form>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
